// src/components/admin/tabs/ToolsTab.js
import React, { useState, useContext, useMemo } from "react";
import { Card, Button, Table, Modal, Form, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { DashboardContext } from "../../../contexts/AdminContext";
import ToolForm from "../forms/ToolForm";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./css/ToolsTab.css";

// Tampilkan rentang OK sesuai tipe toleransi (± atau + / −).
const rangeText = (row) => {
  const num = (v) => Number(String(v).replace(",", "."));
  const n = num(row.nominal);
  if (!Number.isFinite(n)) return "-";
  const clean = (x) => Number(x.toFixed(4)).toString();
  if (row.tol_type === "ASYM") {
    const tp = num(row.tol_plus);
    const tm = num(row.tol_minus);
    if (!Number.isFinite(tp) || !Number.isFinite(tm)) return "-";
    return `${clean(n - tm)} s/d ${clean(n + tp)}`;
  }
  const t = num(row.tolerance);
  if (!Number.isFinite(t)) return "-";
  return `${clean(n - t)} s/d ${clean(n + t)}`;
};

const statusColors = {
  BUTUH_DIPERBAIKI: "danger",
  SEDANG_DIPERBAIKI: "warning",
  SELESAI: "success",
  OK: "success",
  NG: "danger",
  REPAIR: "warning",
};

const ToolsTab = () => {
  const { tools, loading, deleteTool, fetchTools } = useContext(DashboardContext);

  /* ================= EDIT STATUS TOOL (OK / NG) ================= */
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTool, setStatusTool] = useState(null); // { id, part_name, status }
  const [savingStatus, setSavingStatus] = useState(false);

  const openStatusModal = (tool) => {
    setStatusTool({
      id: tool.id,
      part_name: tool.part_name,
      status: (tool.tool_status || "OK").toUpperCase() === "NG" ? "NG" : "OK",
    });
    setShowStatusModal(true);
  };

  const saveToolStatus = async () => {
    if (!statusTool) return;
    setSavingStatus(true);
    try {
      await api.patch(`/tools/${statusTool.id}/status`, {
        status: statusTool.status,
      });
      setShowStatusModal(false);
      if (fetchTools) await fetchTools();
      popup.alert("Status tool berhasil diperbarui ✅");
    } catch (err) {
      console.error(err);
      popup.alert(err?.response?.data?.message || "Gagal memperbarui status tool ❌");
    } finally {
      setSavingStatus(false);
    }
  };
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [showToolModal, setShowToolModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [sectorFilter, setSectorFilter] = useState("ALL");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);

  const [search, setSearch] = useState("");

  /* ================= STANDAR PENGUKURAN ================= */
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [standardTool, setStandardTool] = useState(null);
  const [standardRows, setStandardRows] = useState([]);
  const [loadingStandard, setLoadingStandard] = useState(false);
  const [savingStandard, setSavingStandard] = useState(false);

  // Default otomatis bila admin tidak mengisi
  const DEFAULT_INSTRUMENT = "DRO";
  const DEFAULT_FREQUENCY = "1/Point";

  const newStandardRow = () => ({
    nominal: "",
    tol_type: "PM", // PM = ±, ASYM = + / −
    tolerance: "", // dipakai saat PM
    tol_plus: "", // dipakai saat ASYM
    tol_minus: "", // dipakai saat ASYM
    instrument: DEFAULT_INSTRUMENT,
    frequency_check: DEFAULT_FREQUENCY,
  });

  const openStandardModal = async (tool) => {
    setStandardTool(tool);
    setStandardRows([]);
    setShowStandardModal(true);
    setLoadingStandard(true);
    try {
      const res = await api.get(`/tools/${tool.id}/standards`);
      const rows = res.data || [];
      setStandardRows(
        rows.length
          ? rows.map((r) => {
              const s = (v) =>
                v !== null && v !== undefined ? String(v) : "";
              const isNumRow = r.nominal !== null && r.nominal !== undefined;
              return {
                // Baris angka -> tampilkan nominal; baris teks -> tampilkan requirement
                nominal: isNumRow ? String(r.nominal) : r.requirement || "",
                tol_type: r.tol_type === "ASYM" ? "ASYM" : "PM",
                tolerance: s(r.tolerance),
                tol_plus: s(r.tol_plus),
                tol_minus: s(r.tol_minus),
                instrument: r.instrument || DEFAULT_INSTRUMENT,
                frequency_check: r.frequency_check || DEFAULT_FREQUENCY,
              };
            })
          : [newStandardRow()],
      );
    } catch (err) {
      console.error("LOAD STANDARD ERROR:", err);
      setStandardRows([newStandardRow()]);
    } finally {
      setLoadingStandard(false);
    }
  };

  const updateStandardRow = (index, field, value) => {
    setStandardRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const addStandardRow = () =>
    setStandardRows((prev) => [...prev, newStandardRow()]);

  const removeStandardRow = (index) =>
    setStandardRows((prev) => prev.filter((_, i) => i !== index));

  const saveStandards = async () => {
    if (!standardTool) return;
    try {
      const isNum = (v) =>
        String(v).trim() !== "" &&
        !isNaN(Number(String(v).replace(",", ".")));
      const rowFilled = (r) =>
        String(r.nominal).trim() !== "" ||
        String(r.tolerance).trim() !== "" ||
        String(r.tol_plus).trim() !== "" ||
        String(r.tol_minus).trim() !== "";

      // Nilai Standar boleh angka atau kalimat.
      // Toleransi OPSIONAL, tapi jika diisi harus angka. Judgment otomatis hanya
      // saat Nilai berupa angka DAN toleransi terisi.
      const filledStr = (v) => String(v).trim() !== "";
      for (let i = 0; i < standardRows.length; i++) {
        const r = standardRows[i];
        if (!rowFilled(r)) continue; // baris kosong dilewati
        if (r.tol_type === "ASYM") {
          if (filledStr(r.tol_plus) && !isNum(r.tol_plus)) {
            popup.alert(`Baris ${i + 1}: Toleransi + (atas) harus angka`);
            return;
          }
          if (filledStr(r.tol_minus) && !isNum(r.tol_minus)) {
            popup.alert(`Baris ${i + 1}: Toleransi − (bawah) harus angka`);
            return;
          }
        } else {
          if (filledStr(r.tolerance) && !isNum(r.tolerance)) {
            popup.alert(`Baris ${i + 1}: Toleransi (±) harus angka`);
            return;
          }
        }
      }

      setSavingStandard(true);
      // Instrument & Frekuensi kosong -> isi default otomatis
      const rowsToSave = standardRows
        .filter(rowFilled)
        .map((r) => ({
          nominal: r.nominal,
          tol_type: r.tol_type,
          tolerance: r.tolerance,
          tol_plus: r.tol_plus,
          tol_minus: r.tol_minus,
          instrument: (r.instrument || "").trim() || DEFAULT_INSTRUMENT,
          frequency_check: (r.frequency_check || "").trim() || DEFAULT_FREQUENCY,
        }));
      await api.put(`/tools/${standardTool.id}/standards`, {
        rows: rowsToSave,
      });
      setShowStandardModal(false);
      popup.alert("Standar pengukuran berhasil disimpan ✅");
    } catch (err) {
      console.error("SAVE STANDARD ERROR:", err);
      popup.alert("Gagal menyimpan standar ❌");
    } finally {
      setSavingStandard(false);
    }
  };

  const handleAddClick = () => {
    setEditingTool(null);
    setShowToolModal(true);
  };

  const handleEditClick = (tool) => {
    setEditingTool(tool);
    setShowToolModal(true);
  };

  const handleCloseModal = () => {
    setShowToolModal(false);
    setEditingTool(null);
  };

  const handleDeleteClick = (tool) => {
    setToolToDelete(tool);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (toolToDelete) {
      const result = await deleteTool(toolToDelete.id);
      if (result?.success) {
        setShowDeleteModal(false);
        setToolToDelete(null);
        popup.success("Data berhasil dihapus");
      }
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setToolToDelete(null);
  };

  // =========================
  // FILTER + SEARCH
  // =========================
  const filteredTools = useMemo(() => {
    if (!tools) return [];

    return [...tools]
      .filter((tool) => {
        const keyword = search.toLowerCase();

        const matchSearch =
          tool.part_name?.toLowerCase().includes(keyword) ||
          tool.spec?.toLowerCase().includes(keyword) ||
          tool.kode?.toLowerCase().includes(keyword) ||
          tool.category?.toLowerCase().includes(keyword) ||
          tool.tool_status?.toLowerCase().includes(keyword) ||
          tool.maintenance_status?.toLowerCase().includes(keyword) ||
          tool.sector_name?.toLowerCase().includes(keyword); // 🔹 tambah sector search

        const matchCategory =
          categoryFilter === "ALL" ||
          tool.category?.trim().toUpperCase() === categoryFilter;

        const matchSector =
          sectorFilter === "ALL" ||
          tool.sector_name?.trim() === sectorFilter; // 🔹 filter sector (cocok persis dgn data)

        return matchSearch && matchCategory && matchSector;
      })
      .sort((a, b) => {
        const aStatus = a.maintenance_status || a.tool_status;
        const bStatus = b.maintenance_status || b.tool_status;

        if (aStatus === "OK" && bStatus !== "OK") return 1;
        if (aStatus !== "OK" && bStatus === "OK") return -1;

        if (a.report_time && b.report_time) {
          return new Date(b.report_time) - new Date(a.report_time);
        }

        return b.id - a.id;
      });
  }, [tools, search, categoryFilter, sectorFilter]); // 🔹 tambahkan sectorFilter

  // Daftar sector filter dibangun dari data tools yang ada (hanya sector yang
  // benar-benar dipakai tool). Sector master yang belum punya tool tidak
  // ditampilkan agar filter tetap ringkas. Nama tool sudah diselaraskan dgn
  // master sector, jadi tidak ada lagi duplikat penamaan.
  const sectorOptions = useMemo(() => {
    const set = new Set();
    (tools || []).forEach((t) => {
      const name = t.sector_name?.trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, "id", { numeric: true }),
    );
  }, [tools]);

  /* =========================
     EXPORT EXCEL (mengikuti filter, kategori & pencarian aktif)
  ========================= */
  const handleExportExcel = () => {
    if (!filteredTools.length) {
      popup.alert("Tidak ada data tools untuk diekspor.");
      return;
    }

    const data = filteredTools.map((t, i) => {
      const status = t.maintenance_status || t.tool_status || "-";
      return {
        No: i + 1,
        Kategori: t.category || "-",
        Sector: t.sector_name || "-",
        "Kode / Spec": t.spec || "-",
        "Part Name": t.part_name || "-",
        Status: status,
        "Terakhir Perbaikan": t.report_time
          ? new Date(t.report_time).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : "-",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data Tools");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `data_tools_${Date.now()}.xlsx`);
  };

  if (loading) return <div>Memuat data...</div>;

  return (
    <>
      <Card style={{ marginTop: "-24px" }}>
        <Card.Header
          className="machine-card-header d-flex justify-content-between align-items-center"
          style={{ gap: "8px" }}
        >
          <span style={{ fontSize: "20px" }}>MANAJEMEN TOOLS</span>
          <Form.Select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            size="sm"
            style={{ width: "240px", marginLeft: "10px" }}
          >
            <option value="ALL">Semua Sector</option>
            {sectorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            size="sm"
            style={{ width: "230px" }}
          >
            <option value="ALL">Semua Kategori</option>
            <option value="JIG">JIG</option>
            <option value="NOZEL">NOZEL</option>
            <option value="MOULDING">MOULDING</option>
            <option value="DIES">DIES</option>
            <option value="PIN">PIN</option>
            <option value="HOLE">HOLE</option>
            <option value="BRADING">BRADING</option>
            <option value="PEMILIN">PEMILIN</option>
          </Form.Select>

          <Form.Control
            type="text"
            placeholder="🔍 Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "240px", height: "30px" }}
          />

          <Button
            variant="success"
            size="sm"
            style={{ whiteSpace: "nowrap" }}
            onClick={handleExportExcel}
          >
            Export Excel
          </Button>

          <Button variant="primary" size="sm" onClick={handleAddClick}>
            <FontAwesomeIcon icon={faPlus} /> Tambah Tools
          </Button>
        </Card.Header>

        <Card.Body
          style={{
            height: "80vh",
            padding: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="table-scroll">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th
                    className="text-center"
                    style={{
                      minWidth: "30px",
                      background: "#1e3a8a",
                      color: "#fff",
                    }}
                  >
                    No
                  </th>
                  <th
                    style={{
                      minWidth: "70px",
                      background: "#1e3a8a",
                      color: "#fff",
                    }}
                  >
                    Kategori
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Sector
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Kode / Spec
                  </th>
                  <th
                    style={{
                      minWidth: "130px",
                      background: "#1e3a8a",
                      color: "#fff",
                    }}
                  >
                    Part Name
                  </th>

                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Status
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Trakhir perbaikan
                  </th>
                  <th
                    className="text-center"
                    style={{ background: "#1e3a8a", color: "#fff" }}
                  >
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredTools.map((tool, idx) => {
                  const status = tool.maintenance_status || tool.tool_status;
                  const color = statusColors[status] || "secondary";
                  const lastMaintenance = tool.report_time
                    ? new Date(tool.report_time).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false,
                      })
                    : "-";

                  return (
                    <tr key={tool.id}>
                      <td className="text-center">{idx + 1}</td>
                      <td>{tool.category}</td>
                      <td>{tool.sector_name || "-"}</td>
                      <td className="cell-left">{tool.spec}</td>

                      <td className="cell-left">{tool.part_name}</td>

                      <td className="text-center">
                        <div className="d-flex flex-row align-items-center justify-content-center gap-1">
                          <Badge bg={color}>{status}</Badge>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="py-0 px-2"
                            style={{ fontSize: "11px" }}
                            onClick={() => openStatusModal(tool)}
                          >
                            ✏️ Edit
                          </Button>
                        </div>
                      </td>
                      <td className="text-center">{lastMaintenance}</td>
                      <td className="text-center d-flex">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditClick(tool)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2"
                          onClick={() => handleDeleteClick(tool)}
                        >
                          Hapus
                        </Button>
                        <Button
                          variant="outline-dark"
                          size="sm"
                          onClick={() => openStandardModal(tool)}
                        >
                          📋 Standar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* FORM MODAL */}
      <ToolForm
        show={showToolModal}
        handleClose={handleCloseModal}
        initialData={editingTool}
      />

      {/* EDIT STATUS TOOL MODAL (OK / NG) */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Status Tool</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statusTool && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Tool</Form.Label>
                <Form.Control value={statusTool.part_name || "-"} disabled />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusTool.status}
                  onChange={(e) =>
                    setStatusTool({ ...statusTool, status: e.target.value })
                  }
                >
                  <option value="OK">OK</option>
                  <option value="NG">NG</option>
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowStatusModal(false)}
            disabled={savingStatus}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={saveToolStatus}
            disabled={savingStatus}
          >
            {savingStatus ? "Menyimpan..." : "Simpan Status"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* STANDAR PENGUKURAN MODAL */}
      <Modal
        show={showStandardModal}
        onHide={() => setShowStandardModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Standar Pengukuran
            {standardTool
              ? ` - ${standardTool.part_name || ""} (${standardTool.spec || "-"})`
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingStandard ? (
            <div className="text-center py-3">Memuat...</div>
          ) : (
            <>
              <p className="text-muted" style={{ fontSize: "13px" }}>
                <code>Nilai Standar</code> boleh <strong>angka</strong> atau{" "}
                <strong>kalimat</strong>. Bila angka & toleransi diisi, Judgment
                dihitung otomatis (<code>±</code> simetris / <code>+ / −</code>{" "}
                asimetris) → NG bila pengukuran di luar rentang. Bila berupa
                kalimat (mis. <em>"Visual tidak retak"</em>), disimpan apa adanya
                dan Judgment tidak dihitung otomatis (<code>-</code>).{" "}
                <code>Frekuensi Check</code> juga bebas teks (angka di depan
                menentukan jumlah kolom pengukuran, mis. <code>1/Point</code>).
              </p>
              <Table bordered size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th
                      className="text-center"
                      style={{ width: "40px", background: "#1e3a8a", color: "#fff" }}
                    >
                      No
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Nilai Standar
                    </th>
                    <th
                      style={{ width: "90px", background: "#1e3a8a", color: "#fff" }}
                    >
                      Tipe
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Toleransi
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Rentang OK
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Instrument
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Frekuensi Check
                    </th>
                    <th
                      className="text-center"
                      style={{ width: "50px", background: "#1e3a8a", color: "#fff" }}
                    >
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {standardRows.map((r, i) => (
                    <tr key={i}>
                      <td className="text-center">{i + 1}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={r.nominal}
                          onChange={(e) =>
                            updateStandardRow(i, "nominal", e.target.value)
                          }
                          placeholder="227 atau kalimat"
                        />
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={r.tol_type}
                          onChange={(e) =>
                            updateStandardRow(i, "tol_type", e.target.value)
                          }
                        >
                          <option value="PM">±</option>
                          <option value="ASYM">+ / −</option>
                        </Form.Select>
                      </td>
                      <td>
                        {r.tol_type === "ASYM" ? (
                          <div className="d-flex gap-1">
                            <Form.Control
                              size="sm"
                              type="number"
                              step="any"
                              value={r.tol_plus}
                              onChange={(e) =>
                                updateStandardRow(i, "tol_plus", e.target.value)
                              }
                              placeholder="+ atas"
                              title="Batas atas (+)"
                            />
                            <Form.Control
                              size="sm"
                              type="number"
                              step="any"
                              value={r.tol_minus}
                              onChange={(e) =>
                                updateStandardRow(i, "tol_minus", e.target.value)
                              }
                              placeholder="− bawah"
                              title="Batas bawah (−)"
                            />
                          </div>
                        ) : (
                          <Form.Control
                            size="sm"
                            type="number"
                            step="any"
                            value={r.tolerance}
                            onChange={(e) =>
                              updateStandardRow(i, "tolerance", e.target.value)
                            }
                            placeholder="0.1"
                          />
                        )}
                      </td>
                      <td
                        className="text-center text-muted"
                        style={{ whiteSpace: "nowrap", fontSize: "13px" }}
                      >
                        {rangeText(r)}
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={r.instrument}
                          onChange={(e) =>
                            updateStandardRow(i, "instrument", e.target.value)
                          }
                          placeholder="DRO"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={r.frequency_check}
                          onChange={(e) =>
                            updateStandardRow(
                              i,
                              "frequency_check",
                              e.target.value,
                            )
                          }
                          placeholder="1/Point"
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeStandardRow(i)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addStandardRow}
              >
                <FontAwesomeIcon icon={faPlus} /> Tambah Baris
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowStandardModal(false)}
            disabled={savingStandard}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={saveStandards}
            disabled={savingStandard || loadingStandard}
          >
            {savingStandard ? "Menyimpan..." : "Simpan Standar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Apakah Anda yakin ingin menghapus tool{" "}
            <strong>{toolToDelete?.part_name}</strong>?
          </p>
          <p className="text-danger">Tindakan ini tidak dapat dibatalkan.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ToolsTab;
